// Login route — outside the AppShell/auth guard (see router.tsx's
// `_authenticated` route's `beforeLoad`), so an unauthenticated visitor can
// reach it. With illustrations on, the illustrated wordmark and the form sit
// centered in front of the goblins' workshop; off, it's just the logo and
// the card.

import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { ApiError, ApiUnreachableError, authApi, rateLimitMessage } from '../lib/api-client'
import { useAuth } from '../lib/auth'
import { Field } from '../components/ui'
import { LogoMark } from '../components/logo'
import { useIllustrations } from '../lib/illustrations'
import { safeNextPath } from '../lib/safe-next'

export function LoginPage() {
  const navigate = useNavigate()
  const router = useRouter()
  const { login, logout, token } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [illustrations] = useIllustrations()
  const next = safeNextPath(new URLSearchParams(window.location.search).get('next'))
  const expired = new URLSearchParams(window.location.search).has('expired')

  // Whatever token is stored when this page opens is stale (the auth guard
  // only sends you here without a valid one): clear it once, on arrival —
  // never after signing in, which sets a fresh one.
  useEffect(() => {
    if (token) logout()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (result) => {
      login(result.token)
      // Sent here from a page that needs a session (e.g. the OAuth consent): go back.
      if (next) {
        router.history.push(next)
        return
      }
      // First time in: the guided welcome.
      void navigate({ to: result.user.onboardedAt ? '/dashboard' : '/welcome' })
    },
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    loginMutation.mutate({ email: email.trim(), password })
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-canvas">
      {illustrations ? <div aria-hidden className="login-scene" /> : null}

      <div className="relative mx-auto grid min-h-screen w-full items-center justify-items-center px-4 py-12">
        <div className="w-full max-w-[420px]">
          {illustrations ? (
            <h1 className="mb-4">
              <img
                src="/brand/wordmark.webp"
                alt="Goblic"
                width={1000}
                height={454}
                className="mx-auto h-auto w-full max-w-[420px] select-none drop-shadow-[0_14px_18px_rgb(var(--shadow)/0.16)]"
              />
            </h1>
          ) : (
            <div className="mb-6 flex items-center gap-3">
              <LogoMark size={44} />
              <span className="font-display text-xl font-semibold tracking-[-0.02em] text-ink">
                Goblic
              </span>
            </div>
          )}

          <div className="rounded-[20px] border border-line bg-surface/95 p-8 shadow-overlay backdrop-blur-sm">
            <h2 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
              Sign in
            </h2>
            <p className="mt-1 text-base text-ink-2">
              Run and supervise your coding agents.
            </p>
            {expired ? (
              <p className="mt-4 rounded-[10px] border border-info/30 bg-info-soft px-3 py-2 text-base text-ink">
                Your session expired. Sign in again to continue.
              </p>
            ) : null}

            <form className="mt-7 space-y-4" onSubmit={handleSubmit}>
              <Field label="Email" htmlFor="login-email">
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  className="field-input h-11 rounded-[10px]"
                />
              </Field>
              <Field label="Password" htmlFor="login-password">
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="field-input h-11 rounded-[10px]"
                />
              </Field>

              {loginMutation.isError ? (
                <p
                  className="rounded-[10px] border border-danger/30 bg-danger-soft px-3 py-2 text-base text-ink"
                  role="alert"
                >
                  {loginMutation.error instanceof ApiUnreachableError
                    ? `${loginMutation.error.message} Check that the API is running.`
                    : rateLimitMessage(loginMutation.error)
                      ? rateLimitMessage(loginMutation.error)
                    : loginMutation.error instanceof ApiError &&
                        loginMutation.error.status === 401
                      ? 'That email and password don’t match. Check them and try again.'
                      : `Sign-in failed: ${(loginMutation.error as Error).message}`}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={loginMutation.isPending}
                className="btn-primary h-11 w-full rounded-[10px] text-md"
              >
                {loginMutation.isPending ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
